import { FC } from "react";

export const REPLAY_SPEED_OPTIONS = [1, 2, 5, 10];


export const DEFAULT_REPLAY_SPEED = REPLAY_SPEED_OPTIONS[0];

type Props = {
  // "Replaying events" / "Drawing route" / "Replaying routes" / "Drawing
  // routes" - whichever animation is actually running. resolved by the
  // caller, since which one that is (event replay vs. the shared-clock
  // route draw) differs per screen.
  label: string;
  isPaused: boolean;
  onTogglePause: () => void;
  onStop: () => void;
  progressPercent: number | null;
  // the universal replay clock, already formatted in the account's
  // timezone - null hides the line rather than showing a placeholder
  formattedClock: string | null;
  // true while the clock is racing through a stretch the asset reported
  // nothing in. Replays that compress dead air say so here: the time on
  // screen is still the real time being drawn, and skipping hours of it in
  // a moment is a pacing choice the customer gets to see, not one hidden
  // from them. Left unset by replays that don't compress anything.
  isFastForward?: boolean;
  // the asset's speed at the moment on the clock, already formatted in the
  // account's unit. Distinct from currentSpeed below, which is the replay's
  // own playback rate - left unset by animations that have no asset under
  // the clock, such as the route draw.
  vehicleSpeedLabel?: string | null;
  // A caller that keeps the panel up after its animation has finished - a
  // trip's detail view, where the route IS the screen - passes these. The
  // panel then offers Replay in place of Pause/stop, and the speed buttons
  // become the pace that replay will run at.
  isFinished?: boolean;
  onReplay?: () => void;
  currentSpeed: number;
  onSetSpeed: (speed: number) => void;
  speedOptions?: number[];
  className?: string;
};

// pause/resume, stop, the universal clock, a progress bar and the speed
// buttons - the one panel every replay-style animation on a trip map uses,
// whether that's the main map's Replay All, an event-by-event replay, or a
// detail-view route draw. shared so the three don't drift into three
// slightly different implementations of the same control.
export const ReplayControlsPanel: FC<Props> = ({
  label,
  isPaused,
  onTogglePause,
  onStop,
  progressPercent,
  formattedClock,
  isFastForward = false,
  vehicleSpeedLabel = null,
  isFinished = false,
  onReplay,
  currentSpeed,
  onSetSpeed,
  speedOptions = REPLAY_SPEED_OPTIONS,
  className = "bg-white rounded-lg shadow-md p-3 text-xs w-[220px] space-y-2",
}) => (
  <div className={className}>
    <div className="flex items-center justify-between">
      <span className="font-semibold">{label}</span>
      <div className="flex items-center gap-2 shrink-0">
        {isFinished && onReplay ? (
          <button
            type="button"
            onClick={onReplay}
            className="font-medium text-accent-blue-dark bg-accent-blue-pale rounded-md px-2 py-0.5 hover:bg-accent-blue-paleO66"
          >
            Replay
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={onTogglePause}
              className="font-medium text-accent-blue-dark bg-accent-blue-pale rounded-md px-2 py-0.5 hover:bg-accent-blue-paleO66"
            >
              {isPaused ? "Resume" : "Pause"}
            </button>
            <button
              type="button"
              onClick={onStop}
              className="text-gray-400 hover:text-gray-700 leading-none text-base"
              aria-label="Skip animation"
            >
              &times;
            </button>
          </>
        )}
      </div>
    </div>
    {formattedClock && (
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-heading-gray888">{formattedClock}</span>
        {vehicleSpeedLabel && (
          <span className="font-mono font-semibold text-heading-black">
            {vehicleSpeedLabel}
          </span>
        )}
        {isFastForward && (
          <span className="text-[10px] font-semibold text-heading-gray888 opacity-70">
            &raquo;&raquo; skipping idle
          </span>
        )}
      </div>
    )}
    {!isFinished && (
      <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-accent-blue-dark"
          style={{ width: `${progressPercent ?? 0}%` }}
        />
      </div>
    )}
    <div className="flex items-center gap-1">
      <span className="text-gray-500 mr-1">Speed:</span>
      {speedOptions.map((speed) => (
        <button
          key={speed}
          type="button"
          onClick={() => onSetSpeed(speed)}
          className={`px-2 py-1 rounded-md text-xs font-semibold ${
            currentSpeed === speed
              ? "bg-accent-blue-dark text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          {speed}x
        </button>
      ))}
    </div>
  </div>
);
